import { useEffect, useState, type RefObject } from "react"

export interface ElementSize {
  readonly width: number
  readonly height: number
}

function isRef(target: RefObject<Element | null> | Element | null): target is RefObject<Element | null> {
  return target !== null && "current" in target
}

/**
 * The content size of an element in whole pixels, kept current while it resizes; `null` until it is measured or
 * while there is no element. Pass a ref, or the element itself (from state or a callback ref) when it can be replaced.
 */
export function useElementSize(target: RefObject<Element | null> | Element | null): ElementSize | null {
  const [size, setSize] = useState<ElementSize | null>(null)
  useEffect(() => {
    const element = isRef(target) ? target.current : target
    if (element === null || typeof ResizeObserver === "undefined") {
      setSize(null)
      return
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (entry === undefined) return
      const width = Math.round(entry.contentRect.width)
      const height = Math.round(entry.contentRect.height)
      setSize((current) => (current?.width === width && current.height === height ? current : { width, height }))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [target])
  return size
}
